import React, { useCallback, useEffect, useState } from "react"
import { Alert, Button, Card, CardBody, CardTitle, Col, Input, Row, Table } from "reactstrap"
import { useTranslation } from "react-i18next"
import { get } from "helpers/api_helper"

// TEMPORARY — 4 → 6 digit OTP rollout dashboard: OTP success by flow, length and
// app version. Rule: if 6-digit success is more than ~5 points below 4-digit for a
// flow, switch that flow back to Off.
const RANGES = [7, 14, 30]

const FLOW_LABELS = {
  customerLogin: "Customer login / sign-up",
  customerForgotPassword: "Customer forgot password",
  customerChangeMobile: "Customer change mobile number",
  riderLogin: "Rider login",
  deliveryOtp: "Delivery OTP",
}

const isoDay = date => date.toISOString().slice(0, 10)
const rate = value => (value == null ? "-" : `${value}%`)

// Super admin only: metrics for one tenant (GET superadmin/tenants/:id/otp-metrics).
const TenantOtpRolloutMetricsCard = ({ tenantId }) => {
  const { t } = useTranslation()
  const [days, setDays] = useState(14)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showDaily, setShowDaily] = useState(false)

  const load = useCallback(() => {
    const to = new Date()
    const from = new Date(to.getTime() - (days - 1) * 24 * 60 * 60 * 1000)
    if (!tenantId) return
    setLoading(true)
    get(`superadmin/tenants/${tenantId}/otp-metrics`, {
      params: { from: isoDay(from), to: isoDay(to) },
    })
      .then(response => setData(response?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [days, tenantId])

  useEffect(() => {
    load()
  }, [load])

  const flowLabel = flow => t(FLOW_LABELS[flow] || flow)
  const summary = data?.summary || []
  const rows = data?.rows || []

  return (
    <Row>
      <Col lg={12}>
        <Card>
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <CardTitle className="mb-0">{t("OTP Rollout Monitoring")}</CardTitle>
              <div className="d-flex align-items-center gap-2">
                <Input
                  type="select"
                  bsSize="sm"
                  value={days}
                  onChange={e => setDays(Number(e.target.value))}
                  aria-label={t("Date range")}
                >
                  {RANGES.map(range => (
                    <option key={range} value={range}>
                      {t("Last {{days}} days", { days: range })}
                    </option>
                  ))}
                </Input>
                <Button size="sm" color="light" onClick={load} disabled={loading}>
                  {loading ? t("Loading...") : t("Refresh")}
                </Button>
              </div>
            </div>
            <p className="text-muted">
              <small>
                {t(
                  "Success rate = verified codes / codes sent (including resends). If 6-digit success is more than 5 points below 4-digit for a flow, switch that flow back to Off."
                )}
              </small>
            </p>

            {(data?.alerts || []).map(alert => (
              <Alert color="danger" key={alert.flow} className="py-2">
                {t(
                  "{{flow}}: 6-digit success {{six}}% vs 4-digit {{four}}% ({{drop}} points lower). Consider switching this flow back to Off.",
                  {
                    flow: flowLabel(alert.flow),
                    six: alert.sixDigitSuccessRate,
                    four: alert.fourDigitSuccessRate,
                    drop: alert.drop,
                  }
                )}
              </Alert>
            ))}

            <div className="table-responsive">
              <Table className="align-middle mb-3" size="sm">
                <thead>
                  <tr>
                    <th>{t("Flow")}</th>
                    <th className="text-end">{t("OTP length")}</th>
                    <th className="text-end">{t("Sent")}</th>
                    <th className="text-end">{t("Resent")}</th>
                    <th className="text-end">{t("Verified")}</th>
                    <th className="text-end">{t("Wrong attempts")}</th>
                    <th className="text-end">{t("SMS sent")}</th>
                    <th className="text-end">{t("Success rate")}</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted">
                        {loading ? t("Loading...") : t("No OTP activity in this period")}
                      </td>
                    </tr>
                  ) : (
                    summary.map(item => (
                      <tr key={`${item.flow}-${item.otpLength}`}>
                        <td>{flowLabel(item.flow)}</td>
                        <td className="text-end">{item.otpLength ?? "-"}</td>
                        <td className="text-end">{item.sent}</td>
                        <td className="text-end">{item.resent}</td>
                        <td className="text-end">{item.verified}</td>
                        <td className="text-end">{item.wrongAttempts}</td>
                        <td className="text-end">{item.smsSent}</td>
                        <td className="text-end">{rate(item.successRate)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <Button
              size="sm"
              color="link"
              className="px-0"
              onClick={() => setShowDaily(!showDaily)}
            >
              {showDaily
                ? t("Hide daily breakdown by app version")
                : t("Show daily breakdown by app version")}
            </Button>

            {showDaily ? (
              <div className="table-responsive mt-2">
                <Table className="align-middle mb-0" size="sm">
                  <thead>
                    <tr>
                      <th>{t("Day")}</th>
                      <th>{t("Flow")}</th>
                      <th className="text-end">{t("OTP length")}</th>
                      <th>{t("App version")}</th>
                      <th className="text-end">{t("Sent")}</th>
                      <th className="text-end">{t("Resent")}</th>
                      <th className="text-end">{t("Verified")}</th>
                      <th className="text-end">{t("Wrong attempts")}</th>
                      <th className="text-end">{t("Success rate")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr
                        key={`${row.day}-${row.service}-${row.flow}-${row.otpLength}-${row.appVersion}`}
                      >
                        <td>{row.day}</td>
                        <td>{flowLabel(row.flow)}</td>
                        <td className="text-end">{row.otpLength ?? "-"}</td>
                        <td>{row.appVersion || t("Unknown")}</td>
                        <td className="text-end">{row.sent}</td>
                        <td className="text-end">{row.resent}</td>
                        <td className="text-end">{row.verified}</td>
                        <td className="text-end">{row.wrongAttempts}</td>
                        <td className="text-end">{rate(row.successRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

export default TenantOtpRolloutMetricsCard
